import { create } from 'zustand';
import { useAuthStore } from './auth';

interface InstallPrompt extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}
const OFFERED_KEY = 'camadero.install-offered';
function wasOffered() {
  try {
    return localStorage.getItem(OFFERED_KEY) === '1';
  } catch {
    return false;
  }
}
function rememberOffer() {
  try {
    localStorage.setItem(OFFERED_KEY, '1');
  } catch {
    /* Keep the session preference if storage is unavailable. */
  }
}
interface InstallState {
  contextVersion: number;
  prompt: InstallPrompt | null;
  installed: boolean;
  ios: boolean;
  guide: 'ios' | 'manual' | null;
  eligible: boolean;
  offered: boolean;
  busy: boolean;
  noteSuccessfulLogin(): void;
  markOffered(): void;
  requestInstall(): Promise<void>;
  closeGuide(): void;
}
export const usePwaInstallStore = create<InstallState>((set, get) => ({
  contextVersion: 0,
  prompt: null,
  installed: false,
  ios: false,
  guide: null,
  eligible: false,
  offered: wasOffered(),
  busy: false,
  noteSuccessfulLogin: () => set({ eligible: !get().offered && !get().installed }),
  markOffered: () => {
    rememberOffer();
    set({ offered: true, eligible: false });
  },
  closeGuide: () => set({ guide: null }),
  requestInstall: async () => {
    const state = get();
    const owner = useAuthStore.getState().user?.id;
    if (state.installed || state.busy) return;
    state.markOffered();
    if (!state.prompt) {
      set({ guide: state.ios ? 'ios' : 'manual' });
      return;
    }
    // Invoke in the click handler's user activation; a captured event is single-use.
    set({ prompt: null, busy: true });
    try {
      await state.prompt.prompt();
      await state.prompt.userChoice;
      // Only appinstalled/display-mode confirms installation, not closing the dialog.
    } catch {
      if (
        get().contextVersion === state.contextVersion &&
        useAuthStore.getState().user?.id === owner &&
        !get().installed
      ) {
        set({ guide: 'manual' });
      }
    } finally {
      set({ busy: false });
    }
  },
}));

export function startPwaInstallTracking() {
  const display = window.matchMedia?.('(display-mode: standalone)');
  const ios =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isInstalled = () =>
    !!display?.matches || !!(navigator as Navigator & { standalone?: boolean }).standalone;
  usePwaInstallStore.setState({
    ios,
    installed: isInstalled(),
    offered: usePwaInstallStore.getState().offered || wasOffered(),
  });
  const available = (event: Event) => {
    if (usePwaInstallStore.getState().installed) return;
    event.preventDefault();
    usePwaInstallStore.setState({ prompt: event as InstallPrompt });
  };
  const installed = () => {
    usePwaInstallStore.getState().markOffered();
    usePwaInstallStore.setState({ installed: true, prompt: null, guide: null });
  };
  const displayChanged = () => {
    if (isInstalled()) installed();
    else usePwaInstallStore.setState({ installed: false });
  };
  window.addEventListener('beforeinstallprompt', available);
  window.addEventListener('appinstalled', installed);
  display?.addEventListener('change', displayChanged);
  return () => {
    window.removeEventListener('beforeinstallprompt', available);
    window.removeEventListener('appinstalled', installed);
    display?.removeEventListener('change', displayChanged);
  };
}
